import React, { type ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	getProductAndIDFromARI,
	type JiraStatusCategoryColorType,
	type SearchResultType as SearchClientResultType,
} from '@atlassian/search-client';
import type { IssueLinkedPRsEntry } from '@atlassian/search-client/hydration/pr-types';
import { getJiraUrlWithAnalyticsQueryParams, highlightMatchedText } from '@atlassian/search-common';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { Expand } from '../../../../../../common/ui/expand-list';
import { Assignee as AssigneeComponent } from '../../../../../../common/ui/jira-assignee';
import { getStatus } from '../../../../../../common/ui/jira-status';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getLinksFromText } from '../../../../../../common/ui/link-attachments';
import { LinkedPullRequestsContent } from '../../../../../../common/ui/linked-pull-requests';
import { messages as prMessages } from '../../../../../../common/ui/linked-pull-requests/messages';
import { MentionsLinkedResults } from '../../../../../../common/ui/linked-results';
import { getJiraEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { messages as searchResultTypeMessages } from '../../../../../../common/ui/search-result-type/messages';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchJiraIssue_AtlassianSearchPage$key } from './__generated__/searchJiraIssue_AtlassianSearchPage.graphql';
import { ProjectMetadata } from './project-metadata';

export type SearchJiraIssueProps = SearchResultProps<searchJiraIssue_AtlassianSearchPage$key> & {
	jiraIssueStatus?: JiraIssueStatusAndColorName | null;
	jiraLinkedPRs?: IssueLinkedPRsEntry;
	grouping3pCohort?: string;
};

export const SearchJiraIssue: ({
	node,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	appMode,
	jiraIssueStatus,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchJiraIssueProps) => React.JSX.Element = ({
	node,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	appMode,
	jiraIssueStatus,
	jiraLinkedPRs,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchJiraIssueProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchJiraIssue_AtlassianSearchPage on SearchResultJiraIssue
			@argumentDefinitions(
				includeAssigneeField: { type: "Boolean", defaultValue: false }
				includeIssueTypeField: { type: "Boolean", defaultValue: false }
			) {
				id
				title
				type
				description
				url
				lastModifiedDate
				iconUrl
				issue {
					issueTypeAvatarUrl
					webUrl
					key
					issueTypeField @include(if: $includeIssueTypeField) {
						issueType {
							name
						}
					}
					fieldsById(ids: ["project"]) {
						edges {
							node {
								__typename
								name
								... on JiraProjectField {
									project {
										id
										name
									}
								}
							}
						}
					}
					assigneeField @include(if: $includeAssigneeField) {
						user {
							name
							picture
						}
					}
				}
				__typename
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);
	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const projectField = useMemo(() => {
		if (!data.issue?.fieldsById?.edges) {
			return;
		}
		const fields = data?.issue?.fieldsById?.edges ?? [];
		for (const field of fields) {
			if (
				field &&
				field.node?.__typename === 'JiraProjectField' &&
				field.node?.name === 'Project'
			) {
				return field.node;
			}
		}
	}, [data.issue?.fieldsById?.edges]);

	const projectName = projectField?.project?.name || '';
	const projectId: string | undefined = projectField?.project?.id;

	const url = useMemo(() => {
		const url = data.issue?.webUrl || data.url;
		return getJiraUrlWithAnalyticsQueryParams({
			url: url,
			searchSessionId,
			resultId: data.id,
			projectId,
		});
	}, [data.issue?.webUrl, data.url, data.id, searchSessionId, projectId]);

	const enrichedTitle = useMemo(() => {
		return getTitleWithKeyAndHighlights(searchQuery, data.title, data.issue?.key);
	}, [data.title, data.issue?.key, searchQuery]);

	const enrichedDescription = useMemo(() => {
		return processExcerpt(data.description);
	}, [data.description]);

	const issueType = useMemo(() => {
		if (!fg('rovo_search_jira_issue_type_fps')) {
			return data.type;
		}
		const resolvedType = data.issue?.issueTypeField?.issueType?.name?.toLowerCase();
		if (resolvedType && resolvedType in searchResultTypeMessages) {
			return resolvedType;
		}
		return data.type;
	}, [data.type, data.issue?.issueTypeField?.issueType?.name]);

	const metadataParts: [string, ReactNode][] = useMemo(() => {
		const parts = [
			['type', <SearchResultType type={issueType as SearchClientResultType} />],
			projectName ? ['project', <ProjectMetadata projectName={projectName} />] : null,
			[
				'lastModified',
				data.lastModifiedDate ? <LastModified lastModified={data.lastModifiedDate} /> : null,
			],
			[
				'assignee',
				fg('rovo_search_assignee_field_fps') ? (
					<AssigneeComponent
						user={
							data.issue?.assigneeField?.user
								? {
										name: data.issue.assigneeField.user.name,
										picture: data.issue.assigneeField.user.picture,
									}
								: null
						}
					/>
				) : null,
			],
		];

		return parts.filter(Boolean) as [string, ReactNode][];
	}, [issueType, data.lastModifiedDate, projectName, data.issue?.assigneeField?.user]);

	const titleMetadataParts = useMemo<[string, ReactNode][]>(() => {
		const status =
			jiraIssueStatus !== null &&
			getStatus(jiraIssueStatus?.statusName, jiraIssueStatus?.colorName, !jiraIssueStatus);
		return status ? [['status', status]] : [];
	}, [jiraIssueStatus]);

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	// When frontend hydration gate is on, use PRs from the hydration prop.
	// When off, no PRs are shown.
	const prEntry = FeatureGates.getExperimentValue<boolean>(
		'rovo_fps_jira_github_frontend_hydration',
		'isEnabled',
		false,
	)
		? jiraLinkedPRs
		: undefined;
	const effectivePRs = prEntry?.status === 'loaded' ? prEntry.prs : [];
	const hasMorePRs = prEntry?.status === 'loaded' ? prEntry.hasMore : false;

	const extractedLinksFromText = getLinksFromText(data.description);
	const hasExtractedLinks = extractedLinksFromText && extractedLinksFromText.length > 0;
	const { formatMessage } = useIntl();
	const hasLinkedPRs = effectivePRs.length > 0;
	const hasRelatedLinks =
		(linkedResults && linkedResults.length > 0) || hasExtractedLinks || hasLinkedPRs;

	const icon = getJiraEntityIcon({
		iconUrl: data.iconUrl || data.issue?.issueTypeAvatarUrl,
		type: data.type,
		isIssue: true,
		size: 'medium',
	});

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Jira}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			title={enrichedTitle}
			headerMetadataParts={metadataParts}
			titleMetadata={<SearchMetadata parts={titleMetadataParts} />}
			description={<DescriptionBody description={enrichedDescription} />}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			hasRelatedLinks={hasRelatedLinks}
			footer={
				<MentionsLinkedResults
					linkedResults={linkedResults || []}
					thirdPartyConfigs={thirdPartyConfigs}
					partial={data}
					url={url}
					position={position}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					isAllProductSelected={!selectedProduct}
					additionalExpands={
						hasLinkedPRs ? (
							<Expand
								icon={PullRequestIcon}
								attachmentCount={effectivePRs.length}
								attachmentType="pull-request"
								label={formatMessage(prMessages.expandLabel, {
									count: effectivePRs.length,
									hasMore: hasMorePRs,
								})}
							>
								<LinkedPullRequestsContent pullRequests={effectivePRs} />
							</Expand>
						) : undefined
					}
				/>
			}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchJiraIssue: (node?: any) => node is searchJiraIssue_AtlassianSearchPage$key = (
	node?: any,
): node is searchJiraIssue_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultJiraIssue';
};

export type JiraIssueStatusAndColorName = {
	statusName?: string;
	colorName?: JiraStatusCategoryColorType;
};

export const getTitleWithKeyAndHighlights: (
	searchQuery: string,
	title: string,
	issueKey?: string,
) => React.JSX.Element | React.JSX.Element[] = (
	searchQuery: string,
	title: string,
	issueKey?: string,
) => {
	const enrichedTitle = enrichExcerpt(title);

	// If there is no issueKey or the issue key is already prefixed, simply return the title
	if (!issueKey || title.startsWith(issueKey)) {
		return enrichedTitle;
	}

	// If the issueKey exists in its entirety in the searchQuery, highlight and prefix it
	if (searchQuery.split(' ').includes(issueKey)) {
		const { highlightedTitle: highlightedIssueKey } = highlightMatchedText(
			issueKey,
			searchQuery.split(' '),
		);
		return (
			<>
				{enrichExcerpt(highlightedIssueKey)}: {enrichedTitle}
			</>
		);
	}

	// Otherwise, return the issue key unhighlighted and prefixed
	return (
		<>
			{issueKey}: {enrichedTitle}
		</>
	);
};
